/**
 * 2560×1920 上限へのリサイズ。**拡大は絶対にしない**。
 *
 * - `calculateResizeDimensions` は純粋関数
 * - `resizeToSpec` は OffscreenCanvas で実際の縮小を行う
 *
 * SPEC §2.3 準拠: 元画像が小さい場合は拡大せず、元サイズで返す。
 */

import { CARSENSOR_SPEC, type ResizePlan } from './types';

/**
 * 目標サイズ (2560×1920) に収まるよう縮小計算を行う。
 * アスペクト比は維持する。**拡大はしない** (scale > 1.0 なら noop 扱い)。
 *
 * @param width - 入力幅 (cropTo4x3 済み想定、つまり 4:3)
 * @param height - 入力高さ
 * @param maxWidth - 上限幅 (デフォルト 2560)
 * @param maxHeight - 上限高さ (デフォルト 1920)
 */
export function calculateResizeDimensions(
  width: number,
  height: number,
  maxWidth: number = CARSENSOR_SPEC.MAX_WIDTH,
  maxHeight: number = CARSENSOR_SPEC.MAX_HEIGHT,
): ResizePlan {
  if (width <= 0 || height <= 0) {
    throw new RangeError(`Invalid dimensions: ${width}x${height}`);
  }
  if (maxWidth <= 0 || maxHeight <= 0) {
    throw new RangeError(`Invalid max dimensions: ${maxWidth}x${maxHeight}`);
  }

  const scaleW = maxWidth / width;
  const scaleH = maxHeight / height;
  const scale = Math.min(scaleW, scaleH);

  // 拡大禁止 (scale > 1.0 は元画像がすでに上限以下)
  if (scale >= 1.0) {
    return { width, height, scale: 1.0, noop: true };
  }

  return {
    width: Math.round(width * scale),
    height: Math.round(height * scale),
    scale,
    noop: false,
  };
}

/**
 * ImageBitmap を 2560×1920 に収まるよう縮小する。
 * 元画像が既に上限以下なら元の ImageBitmap をそのまま返す (コピー不要)。
 *
 * ブラウザ / Web Worker でのみ動作 (OffscreenCanvas 必須)。
 */
export async function resizeToSpec(source: ImageBitmap): Promise<ImageBitmap> {
  const plan = calculateResizeDimensions(source.width, source.height);
  if (plan.noop) {
    return source;
  }

  const canvas = new OffscreenCanvas(plan.width, plan.height);
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('OffscreenCanvas 2D context unavailable');
  }
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(source, 0, 0, source.width, source.height, 0, 0, plan.width, plan.height);
  return canvas.transferToImageBitmap();
}
